import { useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { guestLogin } from '../../api/auth';
import { errorMessage } from '../../api/errors';
import { setUser } from '../../store/user.slice';
import { sessionExpiry } from '../../store/session';
import { USER_STORAGE_KEY } from '../../api/client';
import { brand } from '../../app/config';
import { PRIVACY_VERSION } from '../../app/privacy';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export function LoginPage() {
    useDocumentTitle('Accedi');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [mail, setMail] = useState('');
    const [newsletter, setNewsletter] = useState(true);
    const [privacy, setPrivacy] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSubmit = username.trim().length > 0 && privacy && !submitting;

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const data = await guestLogin({
                username: username.trim(),
                mail: mail.trim() || undefined,
                newsletter,
                // M5: il checkbox privacy abilita il submit (`canSubmit`), quindi il
                // consenso arriva sempre al server con timestamp e versione. I nomi
                // sono in snake_case come li attende il server (`privacy_accepted`).
                privacy_accepted: privacy,
                privacy_version: PRIVACY_VERSION,
            });
            const session = {
                logged: true,
                id: data.id,
                username: data.username,
                // M4: la scadenza per l'idratazione locale; il token è nel cookie.
                exp: sessionExpiry(data.expiresIn),
            };
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(session));
            dispatch(setUser(session));
            navigate('/form');
        } catch (err) {
            setError(errorMessage(err, 'Errore di accesso. Verifica i dati inseriti e riprova.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box className="flex min-h-[70vh] items-center justify-center">
            <Paper elevation={0} className="w-full max-w-md border border-white/10 bg-black/40 p-8 backdrop-blur">
                <div className="mb-6 flex flex-col items-center gap-2">
                    <img src={brand.logo} alt={`${brand.name} logo`} className="h-14 w-auto" />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Accedi
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Inserisci il tuo nome per iniziare
                    </Typography>
                </div>

                <div className="flex flex-col gap-4">
                    <TextField
                        label="Inserisci il tuo nome"
                        fullWidth
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                        label="Mail (facoltativa)"
                        fullWidth
                        type="email"
                        value={mail}
                        onChange={(e) => setMail(e.target.value)}
                        helperText="Solo se vuoi ricevere la newsletter"
                    />

                    <FormControlLabel
                        control={<Checkbox checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} />}
                        label={
                            <Typography variant="body2">
                                Iscriviti alla nostra <span className="text-accent">NEWSLETTER</span>
                            </Typography>
                        }
                    />
                    <FormControlLabel
                        control={<Checkbox checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} />}
                        label={
                            <Typography variant="body2">
                                Accetta la normativa <span className="text-accent">PRIVACY</span>
                            </Typography>
                        }
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
                        {submitting ? 'Accesso...' : 'Login!'}
                    </Button>
                </div>
            </Paper>
        </Box>
    );
}
